import React from 'react';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  LAUNCH_PHASE,
  storeLabels,
} from '../../../lib/appLinks';
import styles from './StoreBadges.module.css';

/**
 * The App Store / Google Play CTA pair, using the OFFICIAL badge artwork
 * (public/badges/) — Apple + Google both require their own badges rather
 * than re-drawn logos. Phase-driven (storeLinks.ts LAUNCH_PHASE):
 *   preorder → "Pre-order on the App Store" / "Pre-register on Google Play"
 *   live     → "Download on the App Store"  / "Get it on Google Play"
 *   prelaunch→ non-interactive "Coming soon" text markers (no dead link).
 *
 * The alt text equals the badge's own words (WCAG 2.5.3). Apple's badge
 * comes first (Apple's ordering rule); both render at one height so
 * neither platform is favoured and Google's "same size or larger" rule
 * is met. Badges are never re-coloured, re-typeset, or animated.
 */

type Asset = { src: string; alt: string };

const ASSETS: Record<'preorder' | 'live', { apple: Asset; play: Asset }> = {
  preorder: {
    apple: { src: '/badges/app-store-preorder.svg', alt: 'Pre-order on the App Store' },
    play: { src: '/badges/google-play-preregister.png', alt: 'Pre-register on Google Play' },
  },
  live: {
    apple: { src: '/badges/app-store-download.svg', alt: 'Download on the App Store' },
    play: { src: '/badges/google-play-get.svg', alt: 'Get it on Google Play' },
  },
};

const StoreBadges: React.FC = () => {
  const { apple, play, interactive } = storeLabels();

  // Pre-launch: the store pages 404, so render text markers, never a link.
  if (!interactive || LAUNCH_PHASE === 'prelaunch') {
    return (
      <>
        {[apple, play].map((cta) => (
          <span
            key={cta.name}
            className={styles.soon}
            aria-disabled="true"
          >
            <span className={styles.soonAction}>Coming soon</span>
            <span className={styles.soonStore}>{cta.name}</span>
          </span>
        ))}
      </>
    );
  }

  const set = ASSETS[LAUNCH_PHASE === 'live' ? 'live' : 'preorder'];
  return (
    <>
      {APP_STORE_URL && (
        <a href={APP_STORE_URL} className={styles.badge} aria-label={set.apple.alt}>
          <img className={styles.badgeImg} src={set.apple.src} alt={set.apple.alt} />
        </a>
      )}
      {PLAY_STORE_URL && (
        <a href={PLAY_STORE_URL} className={styles.badge} aria-label={set.play.alt}>
          <img className={styles.badgeImg} src={set.play.src} alt={set.play.alt} />
        </a>
      )}
    </>
  );
};

export default StoreBadges;
